import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Autocomplete,
  Chip,
  Avatar,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  styled,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  createFilterOptions,
} from "@mui/material";
import { Close, Delete as DeleteIcon, Check, AttachFile, InsertDriveFile, Fullscreen, FullscreenExit } from "@mui/icons-material";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  criarCaso,
  atualizarCaso,
  excluirCaso,
  obterCasoCompleto,
  ICriarCasoRequest,
  IAtualizarCasoRequest,
  ICaso,
  ICasoTipologia,
  concluirCaso,
  importarTipologiasDoMOF,
  criarTipologiasNovas,
  copiarTipologiasDeOutroCaso,
  removerTipologiaDoCaso,
  marcarTipologiaComoConcluida,
  IImportarTipologiasMOFRequest,
  ICriarTipologiasNovasRequest,
  ICopiarCasoRequest,
  obterAtividadeBasica,
  IAtividade,
  uploadDocumento,
  listarDocumentos,
  IDocumento,
  excluirDocumento,
} from "services/modules/tarefaDetalhe";
import { getResponsaveisFiltro, IResponsavelFiltro } from "services/modules/quadroTarefas";
import { isEqual } from "lodash";
import { normalizeFormData } from "../utils/dirtyState";
import { useNavigation } from "contexts/notificationsContext";
import { useAuth } from "shared/hooks/auth";
import { ModalImportarTipologiasMOF, ITipologiaMOF } from "./ModalImportarTipologiasMOF";
import { ModalCriarTipologiasNovas } from "./ModalCriarTipologiasNovas";
import { ModalCopiarCasos } from "./ModalCopiarCasos";
import configs from "Config";

// ============================================
// STYLED COMPONENTS
// ============================================

const HeaderAtividade = styled(Box)(({ theme }) => ({
  backgroundColor: "#e8f5e9",
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const CasoHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const ActionButton = styled(IconButton)<{ bgcolor?: string }>(({ bgcolor }) => ({
  backgroundColor: bgcolor || "#e0e0e0",
  color: "#fff !important",
  width: 36,
  height: 36,
  "& svg": {
    color: "#fff !important",
  },
  "&:hover": {
    backgroundColor: bgcolor ? `${bgcolor}dd` : "#bdbdbd",
  },
  "&:disabled": {
    backgroundColor: bgcolor ? `${bgcolor}80` : "#bdbdbd",
    color: "#fff !important",
    "& svg": {
      color: "#fff !important",
    },
    opacity: 0.6,
  },
}));

// ============================================
// INTERFACES
// ============================================

// 24/08 (pedido do PO): em Caso NOVO as tipologias escolhidas ficam PENDENTES na tela
// e só são gravadas ao clicar em SALVAR; sair sem salvar descarta todas as ações.
interface ITipologiaPendente {
  chave: string;
  origem: "mof" | "nova" | "copia";
  codigo: string;
  descricao?: string;
  nomeLinha?: string;
  nomeProjetista?: string;
  idEsquadria?: number; // origem "mof"
  nova?: { codigo: string; descricao: string; idLinha: number; idProjetista: number }; // origem "nova"
  idCasoOrigem?: number; // origem "copia" (aplicada 1x por caso de origem)
}

interface ModalCasoProps {
  isOpen: boolean;
  onClose: () => void;
  idTarefaAtividade: number;
  caso?: ICaso | null; // Se null, é criação
  onCasoSalvo: () => void;
  // N6.2: só na Planificação os recursos de tipologia (copiar/novas/MOF) aparecem
  ehPlanificacao?: boolean;
  // N13.3: referência da tarefa, para listar responsáveis das empresas que a compartilham
  idReferencia?: number | null;
  // Item 5.C: título/descrição da Atividade, usados para pré-preencher um NOVO caso
  // (Descrição = título da Atividade; Detalhes = descrição da Atividade).
  atividadeTitulo?: string | null;
  atividadeDescricao?: string | null;
}

// ============================================
// COMPONENTE
// ============================================

export const ModalCaso = ({
  isOpen,
  onClose,
  idTarefaAtividade,
  caso,
  onCasoSalvo,
  ehPlanificacao = false,
  idReferencia = null,
  atividadeTitulo = null,
  atividadeDescricao = null,
}: ModalCasoProps) => {
  const { openNotification } = useNavigation();
  const { user } = useAuth();
  const isEdicao = !!caso;
  
  // Estado local para caso criado (quando cria novo caso, atualiza este estado)
  const [casoLocal, setCasoLocal] = useState<ICaso | null>(caso || null);

  // Estados do formulário
  const [formData, setFormData] = useState<Partial<ICriarCasoRequest>>({
    descricao: "",
    detalhes: "",
    idsResponsaveis: [],
    idTarefaStatus: 1, // Default: Novos
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Dirty-state (N2.4): snapshot do formulário ao carregar/criar — base para
  // detectar alterações reais (detalhes via stripHtml na normalização)
  const initialFormDataRef = useRef<Partial<ICriarCasoRequest>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Estados para dados relacionados
  const [atividade, setAtividade] = useState<IAtividade | null>(null);
  const [tipologias, setTipologias] = useState<ICasoTipologia[]>([]);
  // 24/08: tipologias escolhidas em Caso NOVO, gravadas somente no SALVAR
  const [tipologiasPendentes, setTipologiasPendentes] = useState<ITipologiaPendente[]>([]);
  
  // Estados para documentos
  const [documentos, setDocumentos] = useState<IDocumento[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Documentos temporários (para novo caso que ainda não foi salvo)
  const [documentosTemporarios, setDocumentosTemporarios] = useState<File[]>([]);
  
  // Estados para modais auxiliares
  const [modalImportarMOFOpen, setModalImportarMOFOpen] = useState(false);
  const [modalCriarTipologiasOpen, setModalCriarTipologiasOpen] = useState(false);
  const [modalCopiarCasosOpen, setModalCopiarCasosOpen] = useState(false);
  
  const quillRef = useRef<ReactQuill>(null);
  // F04R-4 (reteste 21/08): editor de Detalhes expansível — instâncias distintas do
  // Quill (inline e expandida) exigem refs separados, como no PainelComentarios.
  const quillExpandidoRef = useRef<ReactQuill>(null);
  const [editorExpandido, setEditorExpandido] = useState(false);

  // Configuração do filtro de responsáveis para o Autocomplete
  const filterResponsaveis = createFilterOptions<IResponsavelFiltro>({
    matchFrom: "any", // Busca em qualquer parte do texto
    stringify: (option) => option.nome, // Campo usado para busca
  });

  // Configuração do módulo Quill
  const quillModules = useMemo(
    () => ({
      toolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }, { background: [] }],
        ["link", "image"],
        ["clean"],
      ],
    }),
    []
  );

  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "color",
    "background",
    "link",
    "image",
  ];

  // Carregar dados da atividade
  const carregarAtividade = useCallback(async () => {
    if (!idTarefaAtividade) return;
    
    try {
      // Buscar dados básicos da atividade (ID e Título)
      const atividadeBasica = await obterAtividadeBasica(idTarefaAtividade);
      if (atividadeBasica) {
        setAtividade(atividadeBasica);
      }
    } catch (error) {
      console.error("Erro ao carregar atividade:", error);
    }
  }, [idTarefaAtividade]);

  // F04R-3 (reteste 21/08): as opções de Responsável são os responsáveis da SUBTAREFA
  // da atividade (não mais todos os usuários da empresa). Responsáveis antigos já
  // gravados no caso permanecem visíveis (entram nas opções apenas para exibição).
  // Fallback: subtarefa SEM responsáveis (ou atividade legada sem subtarefa) volta à
  // lista da empresa — senão o Caso ficaria impossível de concluir (exige responsável).
  const [responsaveisEmpresa, setResponsaveisEmpresa] = useState<IResponsavelFiltro[]>([]);
  const subtarefaSemResponsaveis =
    !!atividade && (atividade.responsaveisSubtarefa?.length ?? 0) === 0;

  useEffect(() => {
    if (!isOpen || !subtarefaSemResponsaveis || !user?.idEmpresa) return;
    getResponsaveisFiltro(user.idEmpresa, idReferencia ?? undefined)
      .then((responsaveis) =>
        setResponsaveisEmpresa(
          responsaveis.filter(
            (r, index, self) =>
              index === self.findIndex((x) => x.idUsuario === r.idUsuario)
          )
        )
      )
      .catch((error) => console.error("Erro ao carregar responsáveis:", error));
  }, [isOpen, subtarefaSemResponsaveis, user?.idEmpresa, idReferencia]);

  const responsaveisDisponiveis = useMemo<IResponsavelFiltro[]>(() => {
    const daSubtarefa: IResponsavelFiltro[] = (
      atividade?.responsaveisSubtarefa || []
    ).map((r) => ({ idUsuario: r.idUsuario, nome: r.nome }));
    const base = daSubtarefa.length > 0 ? daSubtarefa : responsaveisEmpresa;
    const doCaso: IResponsavelFiltro[] = (casoLocal?.responsaveis || []).map(
      (r) => ({ idUsuario: r.idUsuario, nome: r.nomeUsuario })
    );
    return [...base, ...doCaso].filter(
      (r, index, self) =>
        index === self.findIndex((x) => x.idUsuario === r.idUsuario)
    );
  }, [atividade?.responsaveisSubtarefa, responsaveisEmpresa, casoLocal?.responsaveis]);

  // F04R-3: subtarefa com UM responsável => campo automático e travado.
  const responsavelUnicoSubtarefa =
    (atividade?.responsaveisSubtarefa?.length ?? 0) === 1
      ? atividade!.responsaveisSubtarefa![0]
      : null;

  // Carregar caso completo se estiver editando
  const carregarCaso = useCallback(async () => {
    const casoParaCarregar = casoLocal || caso;
    if (!casoParaCarregar?.idTarefaCaso) return;
    
    try {
      const casoCompleto = await obterCasoCompleto(casoParaCarregar.idTarefaCaso);
      if (casoCompleto) {
        const inicial: Partial<ICriarCasoRequest> = {
          descricao: casoCompleto.descricao,
          detalhes: casoCompleto.detalhes || "",
          idsResponsaveis: casoCompleto.responsaveis.map((r) => r.idUsuario),
          idTarefaStatus: casoCompleto.idTarefaStatus,
        };
        setFormData(inicial);
        // Snapshot do formulário (N2.4): base para detectar alterações reais
        initialFormDataRef.current = inicial;
        setHasChanges(false);
        setTipologias(casoCompleto.tipologias || []);
        setCasoLocal(casoCompleto); // Atualizar caso local
      }
    } catch (error) {
      console.error("Erro ao carregar caso:", error);
    }
  }, [casoLocal?.idTarefaCaso, caso?.idTarefaCaso]);

  // Inicializar dados — reporte de 25/08 (botão EXCLUIR "aparecia e sumia"):
  // o efeito tinha carregarAtividade/carregarCaso nas dependências. Ao salvar um
  // Caso novo, setCasoLocal(criado) recriava carregarCaso (useCallback com dep
  // casoLocal?.idTarefaCaso) e o efeito disparava de novo — com a prop `caso`
  // ainda nula, caindo no ramo "caso novo" e zerando casoLocal, tipologias e
  // pendências. O guard por chave garante uma inicialização por Caso/atividade.
  const chaveInicializacaoRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      chaveInicializacaoRef.current = null;
      return;
    }

    const chave = `${idTarefaAtividade}-${caso?.idTarefaCaso ?? "novo"}`;
    if (chaveInicializacaoRef.current === chave) return;
    chaveInicializacaoRef.current = chave;

    {
      carregarAtividade();

      // Atualizar casoLocal quando prop caso mudar
      setCasoLocal(caso || null);

      if (isEdicao && caso) {
        carregarCaso();
      } else {
        // Item 5.C: ao criar um NOVO caso, pré-preencher a partir da Atividade
        // (Descrição = título; Detalhes = descrição). É apenas default: o usuário
        // pode alterar antes de salvar (não há sincronização contínua).
        const inicial: Partial<ICriarCasoRequest> = {
          descricao: atividadeTitulo ?? atividade?.titulo ?? "",
          detalhes: atividadeDescricao ?? atividade?.descricao ?? "",
          idsResponsaveis: [],
          idTarefaStatus: 1,
        };
        setFormData(inicial);
        // Snapshot do formulário (N2.4): base para detectar alterações reais
        initialFormDataRef.current = inicial;
        setHasChanges(false);
        setTipologias([]);
        setTipologiasPendentes([]); // 24/08: pendências descartadas a cada (re)abertura
        setCasoLocal(null);
        setDocumentosTemporarios([]); // Limpar documentos temporários ao abrir modal
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, idTarefaAtividade, caso?.idTarefaCaso]);

  // F04R-3: subtarefa com um único responsável => preenche automaticamente o Caso
  // (o campo fica travado; snapshot acompanha para não marcar dirty indevido).
  // Somente na CRIAÇÃO: em caso existente vale o que está gravado (evita corrida
  // com carregarCaso e não apaga responsável antigo — risco anotado no plano).
  useEffect(() => {
    if (!isOpen || isEdicao || !responsavelUnicoSubtarefa) return;
    setFormData((prev) => {
      const atuais = prev.idsResponsaveis || [];
      if (
        atuais.length === 1 &&
        atuais[0] === responsavelUnicoSubtarefa.idUsuario
      ) {
        return prev;
      }
      const proximo = {
        ...prev,
        idsResponsaveis: [responsavelUnicoSubtarefa.idUsuario],
      };
      initialFormDataRef.current = {
        ...initialFormDataRef.current,
        idsResponsaveis: [responsavelUnicoSubtarefa.idUsuario],
      };
      return proximo;
    });
  }, [isOpen, responsavelUnicoSubtarefa]);

  // Carregar documentos da atividade
  const carregarDocumentos = useCallback(async () => {
    if (!idTarefaAtividade) return;
    
    try {
      // Buscar idTarefa da atividade para passar para listarDocumentos
      let idTarefaParaBuscar: number | undefined;
      
      if (atividade?.idTarefa) {
        idTarefaParaBuscar = atividade.idTarefa;
      } else {
        // Se atividade ainda não foi carregada, tentar buscar
        const atividadeBasica = await obterAtividadeBasica(idTarefaAtividade);
        if (atividadeBasica?.idTarefa) {
          idTarefaParaBuscar = atividadeBasica.idTarefa;
        }
      }
      
      if (idTarefaParaBuscar) {
        const docs = await listarDocumentos({
          idTarefa: idTarefaParaBuscar,
          idTarefaAtividade: idTarefaAtividade,
        });
        setDocumentos(docs);
      }
    } catch (error) {
      console.error("Erro ao carregar documentos:", error);
    }
  }, [idTarefaAtividade, atividade?.idTarefa]);

  // Carregar documentos quando modal abrir ou atividade mudar
  useEffect(() => {
    if (isOpen && idTarefaAtividade) {
      if (atividade?.idTarefa) {
        // Pequeno delay para garantir que atividade esteja completamente carregada
        const timer = setTimeout(() => {
          carregarDocumentos();
        }, 300);
        return () => clearTimeout(timer);
      } else {
        carregarDocumentos();
      }
    } else {
      // Limpar documentos quando modal fechar
      setDocumentos([]);
    }
  }, [isOpen, idTarefaAtividade, atividade?.idTarefa, carregarDocumentos]);

  // Handlers
  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpar erro do campo
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.descricao?.trim()) {
      newErrors.descricao = "A descrição é obrigatória";
    }

    if (formData.descricao && formData.descricao.length > 300) {
      newErrors.descricao = "A descrição deve ter no máximo 300 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================
  // 24/08: coleta de tipologias PENDENTES (Caso novo — grava só no SALVAR)
  // ============================================

  const handleSelecionarMOFPendente = (itens: ITipologiaMOF[]) => {
    setTipologiasPendentes((prev) => {
      const jaSelecionadas = new Set(
        prev.filter((p) => p.origem === "mof").map((p) => p.idEsquadria)
      );
      const novos: ITipologiaPendente[] = itens
        .filter((t) => !jaSelecionadas.has(t.idEsquadria))
        .map((t) => ({
          chave: `mof-${t.idEsquadria}`,
          origem: "mof",
          codigo: t.codigo,
          descricao: t.descricao,
          nomeLinha: t.nomeLinha,
          nomeProjetista: (t.projetistas || []).join(", ") || "-",
          idEsquadria: t.idEsquadria,
        }));
      return [...prev, ...novos];
    });
  };

  const handleCriarNovasPendente = (
    itens: {
      codigo: string;
      descricao: string;
      idLinha: number;
      idProjetista: number;
      nomeLinha: string;
      nomeProjetista: string;
    }[]
  ) => {
    setTipologiasPendentes((prev) => [
      ...prev,
      ...itens.map((t, i) => ({
        chave: `nova-${prev.length + i}-${t.codigo}`,
        origem: "nova" as const,
        codigo: t.codigo,
        descricao: t.descricao,
        nomeLinha: t.nomeLinha,
        nomeProjetista: t.nomeProjetista,
        nova: {
          codigo: t.codigo,
          descricao: t.descricao,
          idLinha: t.idLinha,
          idProjetista: t.idProjetista,
        },
      })),
    ]);
  };

  const handleCopiarCasoPendente = async (casoOrigem: {
    idTarefaCaso: number;
    descricao: string;
  }) => {
    try {
      // Snapshot das tipologias do caso de origem — só para exibição; a cópia
      // real acontece no SALVAR (copiarTipologiasDeOutroCaso).
      const origem = await obterCasoCompleto(casoOrigem.idTarefaCaso);
      const tipologiasOrigem = origem?.tipologias || [];
      if (tipologiasOrigem.length === 0) {
        openNotification({
          isOpen: true,
          message: "O caso selecionado não possui tipologias para copiar.",
          type: "warning",
        });
        return;
      }
      setTipologiasPendentes((prev) => {
        if (
          prev.some(
            (p) => p.origem === "copia" && p.idCasoOrigem === casoOrigem.idTarefaCaso
          )
        ) {
          return prev;
        }
        return [
          ...prev,
          ...tipologiasOrigem.map((t, i) => ({
            chave: `copia-${casoOrigem.idTarefaCaso}-${i}`,
            origem: "copia" as const,
            codigo: t.codigo || "-",
            descricao: t.descricao,
            nomeLinha: t.nomeLinha || "-",
            nomeProjetista: t.nomeProjetista || "-",
            idCasoOrigem: casoOrigem.idTarefaCaso,
          })),
        ];
      });
    } catch (error) {
      console.error("Erro ao carregar tipologias do caso de origem:", error);
    }
  };

  const handleRemoverPendente = (pendente: ITipologiaPendente) => {
    setTipologiasPendentes((prev) =>
      pendente.origem === "copia"
        ? prev.filter((p) => p.idCasoOrigem !== pendente.idCasoOrigem)
        : prev.filter((p) => p.chave !== pendente.chave)
    );
  };

  // Aplica as pendências no Caso recém-criado (chamado pelo SALVAR da criação)
  const aplicarTipologiasPendentes = async (idNovoCaso: number) => {
    const idsMof = tipologiasPendentes
      .filter((p) => p.origem === "mof")
      .map((p) => p.idEsquadria!);
    if (idsMof.length > 0) {
      await importarTipologiasDoMOF(idNovoCaso, {
        idTarefaCaso: idNovoCaso,
        idsTipologias: idsMof,
      });
    }

    const novas = tipologiasPendentes
      .filter((p) => p.origem === "nova")
      .map((p) => p.nova!);
    if (novas.length > 0) {
      await criarTipologiasNovas(idNovoCaso, {
        idTarefaCaso: idNovoCaso,
        tipologias: novas,
      } as ICriarTipologiasNovasRequest);
    }

    const casosOrigem = Array.from(
      new Set(
        tipologiasPendentes
          .filter((p) => p.origem === "copia")
          .map((p) => p.idCasoOrigem!)
      )
    );
    for (const idOrigem of casosOrigem) {
      await copiarTipologiasDeOutroCaso(idNovoCaso, {
        idTarefaCasoOrigem: idOrigem,
        idTarefaCasoDestino: idNovoCaso,
      });
    }

    setTipologiasPendentes([]);
  };

  // Retorna true quando o Caso foi salvo com sucesso.
  const handleSalvar = async (): Promise<boolean> => {
    if (!validateForm()) return false;

    setLoading(true);
    try {
      if (isEdicao && caso) {
        // Atualizar
        const request: IAtualizarCasoRequest = {
          idTarefaCaso: caso.idTarefaCaso,
          descricao: formData.descricao || "",
          detalhes: formData.detalhes || null,
          idsResponsaveis: formData.idsResponsaveis || [],
          idTarefaStatus: formData.idTarefaStatus || 1,
        };

        await atualizarCaso(caso.idTarefaCaso, request);
        openNotification({
          isOpen: true,
          message: "Caso atualizado com sucesso!",
          type: "success",
        });
      } else {
        // Criar
        // Garantir que descricao não seja vazia (já validado, mas garantir)
        if (!formData.descricao?.trim()) {
          openNotification({
            isOpen: true,
            message: "A descrição é obrigatória",
            type: "error",
          });
          setLoading(false);
          return false;
        }

        const request: ICriarCasoRequest = {
          idTarefaAtividade: idTarefaAtividade, // Incluir para compatibilidade com backend
          descricao: formData.descricao.trim(),
          detalhes: formData.detalhes?.trim() || null,
          idsResponsaveis: formData.idsResponsaveis || [],
          idTarefaStatus: formData.idTarefaStatus && formData.idTarefaStatus > 0 ? formData.idTarefaStatus : 1,
        };

        console.log("[ModalCaso] Criando caso com request:", {
          idTarefaAtividade,
          request,
        });

        const casoCriado = await criarCaso(idTarefaAtividade, request);

        // 24/08: gravar agora as tipologias que ficaram pendentes na tela
        if (casoCriado?.idTarefaCaso && tipologiasPendentes.length > 0) {
          try {
            await aplicarTipologiasPendentes(casoCriado.idTarefaCaso);
          } catch (error) {
            console.error("Erro ao gravar tipologias pendentes:", error);
            openNotification({
              isOpen: true,
              message: "Caso criado, mas houve erro ao gravar algumas tipologias. Confira a lista.",
              type: "warning",
            });
          }
        }

        // Fazer upload dos documentos temporários com o idTarefaAtividade correto
        if (documentosTemporarios.length > 0 && atividade?.idTarefa) {
          try {
            setIsUploading(true);
            console.log("[ModalCaso] Fazendo upload de", documentosTemporarios.length, "documento(s) para atividade", idTarefaAtividade);
            
            for (const file of documentosTemporarios) {
              console.log("[ModalCaso] Upload documento:", file.name, "para idTarefaAtividade:", idTarefaAtividade);
              const uploadResult = await uploadDocumento(atividade.idTarefa, undefined, file, idTarefaAtividade);
              console.log("[ModalCaso] Documento enviado com sucesso:", uploadResult);
            }
            
            const quantidadeDocumentos = documentosTemporarios.length;
            setDocumentosTemporarios([]);
            
            // Aguardar um pouco para garantir que o banco processou o commit
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Recarregar documentos para exibir os que foram anexados
            console.log("[ModalCaso] Recarregando documentos após upload...");
            try {
              const docs = await listarDocumentos({
                idTarefa: atividade.idTarefa,
                idTarefaAtividade: idTarefaAtividade,
              });
              console.log("[ModalCaso] Documentos carregados após upload:", docs);
              setDocumentos(docs);
            } catch (error) {
              console.error("Erro ao carregar documentos após upload:", error);
            }
            
            openNotification({
              isOpen: true,
              message: `Caso criado e ${quantidadeDocumentos} documento(s) anexado(s) com sucesso!`,
              type: "success",
            });
          } catch (error) {
            console.error("Erro ao fazer upload dos documentos temporários:", error);
            openNotification({
              isOpen: true,
              message: "Caso criado, mas houve erro ao anexar alguns documentos.",
              type: "warning",
            });
          } finally {
            setIsUploading(false);
          }
        } else {
          openNotification({
            isOpen: true,
            message: "Caso criado com sucesso!",
            type: "success",
          });
        }
        
        // Atualizar casoLocal para habilitar botões de tipologias.
        // criarCaso já retorna um ICaso válido (completo ou mínimo best-effort),
        // então usamos esse retorno como base garantida; um refetch adicional
        // apenas enriquece quando disponível.
        if (casoCriado?.idTarefaCaso) {
          const casoCompleto = await obterCasoCompleto(casoCriado.idTarefaCaso);
          const casoParaEstado = casoCompleto ?? casoCriado;
          setCasoLocal(casoParaEstado);
          setTipologias(casoParaEstado.tipologias || []);
        }
        
        // Notificar componente pai mas não fechar modal - permitir adicionar tipologias
        onCasoSalvo();
        return true; // Não fechar modal
      }

      // Item 6.1: NÃO fechar o modal ao Salvar (edição) — manter aberto.
      // O fechamento ocorre apenas pelo X/Cancelar (a criação já mantém aberto).
      onCasoSalvo();
      return true;
    } catch (error: any) {
      openNotification({
        isOpen: true,
        message: error.message || "Erro ao salvar caso",
        type: "error",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };


  // 24/08 (pedido do PO): excluir o Caso pela própria tela (exclusão lógica no
  // backend — endpoint DELETE casos/{id}, com as sub-permissões da Fase 04).
  const handleExcluirCaso = async () => {
    const idParaExcluir = casoLocal?.idTarefaCaso ?? caso?.idTarefaCaso;
    if (!idParaExcluir) return;
    if (
      !window.confirm(
        "Deseja realmente excluir este Caso? Ele deixará de ser exibido, junto com suas tipologias."
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await excluirCaso(idParaExcluir);
      openNotification({
        isOpen: true,
        message: "Caso excluído com sucesso!",
        type: "success",
      });
      onCasoSalvo(); // atualiza a lista de casos da atividade
      onClose();
    } catch (error: any) {
      openNotification({
        isOpen: true,
        message: error.message || "Erro ao excluir caso",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConcluirCaso = async () => {
    if (!casoAtual?.idTarefaCaso) return;
    
    // Validação 1: Responsável obrigatório
    if (!formData.idsResponsaveis || formData.idsResponsaveis.length === 0) {
      openNotification({
        isOpen: true,
        message: "É necessário ter pelo menos um responsável para concluir o caso.",
        type: "warning",
      });
      return;
    }

    // Validação 2: Todas as tipologias devem estar concluídas
    if (tipologias && tipologias.length > 0) {
      const tipologiasNaoConcluidas = tipologias.filter((t) => !t.concluido);
      
      if (tipologiasNaoConcluidas.length > 0) {
        openNotification({
          isOpen: true,
          message: `Não é possível concluir o caso. Existem ${tipologiasNaoConcluidas.length} tipologia(s) não concluída(s). Conclua todas as tipologias antes de concluir o caso.`,
          type: "warning",
        });
        return;
      }
    }

    try {
      console.log("[handleConcluirCaso] Iniciando conclusão. Caso:", casoAtual.idTarefaCaso);
      console.log("[handleConcluirCaso] Responsáveis:", formData.idsResponsaveis);
      console.log("[handleConcluirCaso] Tipologias:", tipologias);
      console.log("[handleConcluirCaso] Tipologias concluídas:", tipologias.filter((t) => t.concluido).length, "de", tipologias.length);
      
      // IMPORTANTE: Sempre salvar o caso antes de concluir para garantir que os responsáveis estejam no banco
      // Isso garante que qualquer alteração feita no formulário (incluindo responsáveis) seja persistida
      console.log("[handleConcluirCaso] Salvando caso antes de concluir para garantir sincronização...");
      
      const request: IAtualizarCasoRequest = {
        idTarefaCaso: casoAtual.idTarefaCaso,
        descricao: formData.descricao || casoLocal?.descricao || "",
        detalhes: formData.detalhes || casoLocal?.detalhes || null,
        idsResponsaveis: formData.idsResponsaveis || [],
        idTarefaStatus: formData.idTarefaStatus || casoLocal?.idTarefaStatus || 1,
      };
      
      await atualizarCaso(casoAtual.idTarefaCaso, request);
      console.log("[handleConcluirCaso] Caso atualizado com sucesso. Agora concluindo...");
      
      // Aguardar um pequeno delay para garantir que o banco processou a atualização
      await new Promise(resolve => setTimeout(resolve, 100));
      
      await concluirCaso(casoAtual.idTarefaCaso);
      
      // Atualizar estado local
      if (casoLocal) {
        setCasoLocal({ ...casoLocal, concluido: true });
      }
      
      openNotification({
        isOpen: true,
        message: "Caso concluído com sucesso!",
        type: "success",
      });
      onCasoSalvo();
    } catch (error: any) {
      console.error("[handleConcluirCaso] Erro capturado:", error);
      console.error("[handleConcluirCaso] Erro.message:", error.message);
      console.error("[handleConcluirCaso] Erro.response:", error.response);
      console.error("[handleConcluirCaso] Erro.response?.data:", error.response?.data);
      
      // Extrair mensagem de erro de forma mais robusta
      let errorMessage = "Erro ao concluir caso";
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }
      
      openNotification({
        isOpen: true,
        message: errorMessage,
        type: "error",
      });
    }
  };

  // Handlers para documentos
  const handleUploadDocumento = async (file: File) => {
    setIsUploading(true);
    try {
      // Se estamos editando um caso existente, fazer upload imediatamente
      if (isEdicao && atividade?.idTarefa) {
        await uploadDocumento(atividade.idTarefa, undefined, file, idTarefaAtividade);
        openNotification({
          isOpen: true,
          message: "Documento anexado com sucesso!",
          type: "success",
        });
        await carregarDocumentos();
      } else {
        // Se estamos criando um novo caso, armazenar temporariamente
        // O upload será feito após criar o caso
        setDocumentosTemporarios((prev) => [...prev, file]);
        openNotification({
          isOpen: true,
          message: "Documento será anexado após salvar o caso.",
          type: "info",
        });
      }
    } catch (error) {
      console.error("Erro ao anexar documento:", error);
      openNotification({
        isOpen: true,
        message: "Não foi possível anexar o documento.",
        type: "error",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleExcluirDocumento = async (idDocumento: number) => {
    if (!window.confirm("Deseja excluir este documento?")) return;

    try {
      await excluirDocumento(idDocumento);
      openNotification({
        isOpen: true,
        message: "Documento excluído com sucesso!",
        type: "success",
      });
      await carregarDocumentos();
    } catch (error) {
      console.error("Erro ao excluir documento:", error);
      openNotification({
        isOpen: true,
        message: "Não foi possível excluir o documento.",
        type: "error",
      });
    }
  };

  // Remover documento temporário
  const handleRemoverDocumentoTemporario = (index: number) => {
    setDocumentosTemporarios((prev) => prev.filter((_, i) => i !== index));
  };

  // Função para inserir link de documento no editor
  const inserirLinkDocumento = useCallback((documento: IDocumento) => {
    // F04R-4: com o editor expansível há duas instâncias do Quill; o link vai
    // para a que está visível (expandida ou inline).
    const refAtivo = editorExpandido ? quillExpandidoRef : quillRef;
    if (!refAtivo.current) {
      console.warn("[inserirLinkDocumento] quillRef não está disponível");
      return;
    }

    try {
      const quill = refAtivo.current.getEditor();
      if (!quill) {
        console.warn("[inserirLinkDocumento] Editor Quill não está disponível");
        return;
      }

      const range = quill.getSelection(true);
      // Usar a URL base da API configurada (mesma base usada pelo serviço)
      const apiBaseUrl = configs.REACT_APP_BASE_URL || window.location.origin;
      // Remover /api do final se existir, pois vamos adicionar o caminho completo
      const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
      const link = `${baseUrl}/api/TarefaDetalhe/DownloadDocumento/${documento.idTarefaDocumento}`;
      
      if (range) {
        quill.insertText(range.index, documento.nomeOriginal, 'link', link);
        const newIndex = range.index + documento.nomeOriginal.length;
        quill.setSelection(newIndex, 0);
      } else {
        // Se não há seleção, inserir no final
        const length = quill.getLength();
        quill.insertText(length - 1, documento.nomeOriginal, 'link', link);
        const newIndex = length - 1 + documento.nomeOriginal.length;
        quill.setSelection(newIndex, 0);
      }
    } catch (error) {
      console.error("[inserirLinkDocumento] Erro ao inserir link:", error);
      openNotification({
        isOpen: true,
        message: "Não foi possível inserir o link no editor.",
        type: "error",
      });
    }
  }, [openNotification, editorExpandido]);

  // Dirty-state por snapshot (N2.4): compara o formulário atual (normalizado)
  // com o snapshot capturado ao carregar/criar. Aqui "detalhes" é o campo rico
  // (Quill); "descricao" é TextField simples.
  useEffect(() => {
    setHasChanges(
      !isEqual(
        normalizeFormData(formData, ["detalhes"], []),
        normalizeFormData(initialFormDataRef.current, ["detalhes"], [])
      ) || tipologiasPendentes.length > 0 // 24/08: pendências contam como alteração não salva
    );
  }, [formData, tipologiasPendentes]);

  // Fechamento com guard (N2.4): confirma descarte quando há alterações reais
  const handleClose = useCallback(() => {
    if (
      hasChanges &&
      !window.confirm(
        "Há alterações não salvas. Deseja realmente fechar sem salvar?"
      )
    ) {
      return;
    }
    onClose();
  }, [hasChanges, onClose]);

  const temResponsavel = (formData.idsResponsaveis?.length || 0) > 0;
  const casoAtual = casoLocal || caso; // Usar casoLocal se disponível
  // Id efetivo do Caso já gravado: vale tanto o estado local quanto a prop, para
  // que as ações de Caso existente (ex.: EXCLUIR) não dependam de um único estado.
  const idCasoAtual = casoLocal?.idTarefaCaso ?? caso?.idTarefaCaso ?? null;

  // ============================================
  // VALIDAÇÕES DE TIPOLOGIAS
  // ============================================
  
  /**
   * Verifica se todas as tipologias estão concluídas
   * Se não há tipologias, retorna true (permite concluir/mudar status)
   * Se há tipologias, retorna true apenas se todas estiverem concluídas
   */
  const todasTipologiasConcluidas = useMemo(() => {
    if (!tipologias || tipologias.length === 0) {
      // Se não há tipologias, permite concluir/mudar status
      return true;
    }
    
    // Se há tipologias, todas devem estar concluídas
    const todasConcluidas = tipologias.every((t) => t.concluido === true);
    
    console.log("[ModalCaso] Validação de tipologias:", {
      totalTipologias: tipologias.length,
      concluidas: tipologias.filter((t) => t.concluido).length,
      todasConcluidas,
    });
    
    return todasConcluidas;
  }, [tipologias]);

  // F04R-3/F04R-4: o campo Status saiu do layout; a trava de tipologias segue
  // valendo para o botão Concluir (podeConcluirCaso).
  const podeConcluirCaso = useMemo(() => {
    // Verificações básicas
    if (!casoAtual?.idTarefaCaso) return false;
    if (!temResponsavel) return false;
    if (casoAtual?.concluido) return false;
    
    // Validação de tipologias: só pode concluir se todas estiverem concluídas
    if (!todasTipologiasConcluidas) return false;
    
    return true;
  }, [casoAtual, temResponsavel, todasTipologiasConcluidas]);

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, p: 0 }}>
        <Box sx={{ width: "100%", p: 2 }}>
          {/* Título do Caso - Primeiro */}
          <CasoHeader>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {casoAtual?.idTarefaCaso ? `Caso ${casoAtual.idTarefaCaso}` : "Novo Caso"}
            </Typography>
          </CasoHeader>

          {/* Header da Atividade */}
          {atividade && (
            <HeaderAtividade>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Atividade {atividade.idTarefaAtividade}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {atividade.titulo || "Sem título"}
              </Typography>
            </HeaderAtividade>
          )}
        </Box>
        <IconButton
          aria-label="fechar"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Painel Esquerdo - Campo Descrição ocupa toda a largura */}
          <Grid item xs={12}>
            {/* Linha com Botões e Campo Descrição */}
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
              {/* Botões de ação */}
              <Box display="flex" gap={1}>
                {/* Botão Concluir (✓ verde) - Fundo verde com ícone branco conforme mockup */}
                <Tooltip 
                  title={
                    !casoAtual?.idTarefaCaso 
                      ? "Salve o caso antes de concluir"
                      : !temResponsavel 
                      ? "É necessário ter pelo menos um responsável para concluir o caso"
                      : casoAtual?.concluido
                      ? "Caso já está concluído"
                      : !todasTipologiasConcluidas
                      ? `Não é possível concluir o caso. Existem ${tipologias.filter((t) => !t.concluido).length} tipologia(s) não concluída(s).`
                      : "Concluir caso"
                  }
                >
                  <span>
                    <ActionButton
                      bgcolor="#4caf50"
                      onClick={handleConcluirCaso}
                      disabled={!podeConcluirCaso}
                      sx={{
                        backgroundColor: "#4caf50 !important",
                        "& svg": {
                          color: "#fff !important",
                        },
                        "&:hover": {
                          backgroundColor: "#45a049 !important",
                        },
                        "&:disabled": {
                          backgroundColor: "#81c784 !important",
                          opacity: 0.6,
                        },
                      }}
                    >
                      <Check fontSize="small" sx={{ color: "#fff !important" }} />
                    </ActionButton>
                  </span>
                </Tooltip>

              </Box>

              {/* Campo Descrição - Ocupa todo o espaço restante */}
              <TextField
                autoFocus
                margin="dense"
                label="Descrição *"
                type="text"
                fullWidth
                variant="outlined"
                size="small"
                value={formData.descricao}
                onChange={(e) => handleChange("descricao", e.target.value)}
                error={!!errors.descricao}
                helperText={errors.descricao}
                inputProps={{ maxLength: 300 }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Grid>

          {/* Painel Esquerdo - Campos */}
          {/* Item 9 (mockup 21/08): coluna esquerda larga como no mockup (~45%) */}
          <Grid item xs={12} md={5}>
            {/* Campo Responsável — F04R-3: 1 responsável na subtarefa = automático e
                travado; vários = seleção restrita a eles */}
            <Autocomplete
              multiple
              disabled={!!responsavelUnicoSubtarefa}
              options={responsaveisDisponiveis}
              getOptionLabel={(option) => option.nome}
              filterOptions={filterResponsaveis}
              isOptionEqualToValue={(option, value) => option.idUsuario === value.idUsuario}
              value={responsaveisDisponiveis.filter((r) =>
                formData.idsResponsaveis?.includes(r.idUsuario)
              )}
              onChange={(_, newValue) => {
                handleChange(
                  "idsResponsaveis",
                  newValue.map((v) => v.idUsuario)
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Responsável"
                  margin="dense"
                  size="small"
                  variant="outlined"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.idUsuario}
                    label={option.nome}
                    avatar={
                      <Avatar sx={{ width: 24, height: 24, fontSize: "0.75rem" }}>
                        {option.nome
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .substring(0, 2)
                          .toUpperCase()}
                      </Avatar>
                    }
                  />
                ))
              }
              sx={{ mb: 2 }}
            />

            {/* F04R-3/F04R-4 (reteste 21/08, item 9): o campo Status foi removido do
                layout — formData.idTarefaStatus continua carregado e enviado nos saves,
                e o card segue movendo pelo quadro de atividades. */}

            {/* Campo Detalhes do caso (Editor Rico) — item 9 (mockup 21/08): editor
                alto acompanhando a lista de tipologias; ⛶ no canto da toolbar */}
            <Box sx={{ mb: 2, position: "relative" }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Detalhes do caso
              </Typography>
              <Tooltip title="Expandir editor">
                <IconButton
                  size="small"
                  onClick={() => setEditorExpandido(true)}
                  sx={{ position: "absolute", top: 28, right: 4, zIndex: 1 }}
                >
                  <Fullscreen fontSize="small" />
                </IconButton>
              </Tooltip>
              <ReactQuill
                theme="snow"
                value={formData.detalhes || ""}
                onChange={(value) => handleChange("detalhes", value)}
                modules={quillModules}
                formats={quillFormats}
                style={{
                  height: "480px",
                  marginBottom: "40px",
                }}
                ref={quillRef}
              />
            </Box>

            {/* Seção de Anexar Documentos */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Documentos Anexados
              </Typography>
              
              {/* Botão para anexar documento */}
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleUploadDocumento(file);
                  }
                }}
                disabled={isUploading}
              />
              <Button
                variant="outlined"
                startIcon={<AttachFile />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                size="small"
                sx={{ mb: 2 }}
              >
                {isUploading ? "Anexando..." : "Anexar Documento"}
              </Button>

              {/* Lista de documentos temporários (apenas quando criando novo caso) */}
              {!isEdicao && documentosTemporarios.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Documentos serão anexados após salvar o caso:
                  </Typography>
                  <List dense sx={{ bgcolor: "#fff3cd", borderRadius: 1, p: 1, border: "1px solid #ffc107" }}>
                    {documentosTemporarios.map((file, index) => (
                      <ListItem
                        key={`temp-${index}`}
                        sx={{
                          py: 0.5,
                          "&:hover": { bgcolor: "#ffe082" },
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoverDocumentoTemporario(index)}
                            disabled={loading || isUploading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          <InsertDriveFile fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {file.name}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {(file.size / 1024).toFixed(2)} KB
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Lista de documentos salvos */}
              {documentos.length > 0 && (
                <List dense sx={{ bgcolor: "#f5f5f5", borderRadius: 1, p: 1 }}>
                  {documentos.map((doc) => (
                    <ListItem
                      key={doc.idTarefaDocumento}
                      sx={{
                        py: 0.5,
                        "&:hover": { bgcolor: "#e0e0e0" },
                        cursor: "pointer",
                      }}
                      onClick={() => inserirLinkDocumento(doc)}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExcluirDocumento(doc.idTarefaDocumento);
                          }}
                          disabled={loading || isUploading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemIcon>
                        <InsertDriveFile fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => inserirLinkDocumento(doc)}
                          >
                            {doc.nomeOriginal}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {new Date(doc.dataUpload).toLocaleDateString("pt-BR")}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {/* F04R-4 (reteste 21/08, item 9): indicação sobre clique no documento
                  removida do layout a pedido do PO. */}
            </Box>
          </Grid>

          {/* Painel Direito - Botões de Tipologias e Lista */}
          {/* Item 9 (mockup 21/08): lista à direita (~55%) com divisor vertical */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{ borderLeft: { md: "1px solid" }, borderColor: { md: "divider" } }}
          >
            {/* Botões de ação de tipologias — N6.2: só na Planificação */}
            {ehPlanificacao && (
              <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setModalCopiarCasosOpen(true)}
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#FF9800", // Laranja
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#F57C00",
                    },
                    "&:disabled": {
                      backgroundColor: "#FFB74D",
                      color: "white",
                    },
                  }}
                >
                  + COPIAR OUTROS CASOS
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => setModalCriarTipologiasOpen(true)}
                  disabled={loading}
                  sx={{ textTransform: "none" }}
                >
                  + NOVA(S) TIPOLOGIA(S)
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setModalImportarMOFOpen(true)}
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#FF9800", // Laranja
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#F57C00",
                    },
                    "&:disabled": {
                      backgroundColor: "#FFB74D",
                      color: "white",
                    },
                  }}
                >
                  + CADASTRO TIPOLOGIAS MOF
                </Button>
              </Box>
            )}

            {/* Lista de Tipologias (persistidas + pendentes do Caso novo) */}
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Lista de tipologias
            </Typography>
            {tipologias.length + tipologiasPendentes.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  border: "1px dashed #ccc",
                  borderRadius: 1,
                  minHeight: 200,
                }}
              />
            ) : (
              <TableContainer 
                component={Paper} 
                variant="outlined"
                sx={{ 
                  maxHeight: 500,
                  overflowX: "auto",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    height: 8,
                    width: 8,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#ccc",
                    borderRadius: 4,
                  },
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={50} sx={{ minWidth: 50 }}>#</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Código</TableCell>
                      {/* Item 9 (mockup 21/08): sem a coluna Descrição — é ela que
                          forçava a lista a ocupar 75% do modal */}
                      <TableCell sx={{ minWidth: 120 }}>Linha</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Projetista</TableCell>
                      <TableCell width={80} sx={{ minWidth: 80 }}>Novo</TableCell>
                      <TableCell width={100} sx={{ minWidth: 100 }}>Concluído</TableCell>
                      <TableCell width={80} sx={{ minWidth: 80 }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tipologias.map((tipologia, index) => (
                      <TableRow key={tipologia.idTarefaCasoTipologia}>
                        <TableCell>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              backgroundColor: "#3C90AD",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                            }}
                          >
                            {index + 1}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {/* Descrição saiu da grade (item 9); fica acessível no tooltip do código */}
                          <Tooltip title={tipologia.descricao || ""} arrow>
                            <Typography variant="body2">{tipologia.codigo}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{tipologia.nomeLinha || "-"}</TableCell>
                        <TableCell>{tipologia.nomeProjetista || "-"}</TableCell>
                        <TableCell>{tipologia.novo ? "Sim" : "Não"}</TableCell>
                        <TableCell>
                          <Checkbox
                            checked={tipologia.concluido}
                            onChange={async (e) => {
                              if (casoAtual?.idTarefaCaso) {
                                const novoValor = e.target.checked;
                                const valorAnterior = tipologia.concluido;
                                
                                // Atualização otimista: atualizar estado local IMEDIATAMENTE
                                setTipologias((prev) =>
                                  prev.map((t) =>
                                    t.idTarefaCasoTipologia === tipologia.idTarefaCasoTipologia
                                      ? { ...t, concluido: novoValor }
                                      : t
                                  )
                                );
                                
                                try {
                                  // Fazer chamada à API após atualizar o estado
                                  await marcarTipologiaComoConcluida(
                                    casoAtual.idTarefaCaso,
                                    tipologia.idTarefaCasoTipologia,
                                    novoValor
                                  );
                                } catch (error) {
                                  // Em caso de erro, reverter para o valor anterior
                                  console.error("Erro ao marcar tipologia:", error);
                                  setTipologias((prev) =>
                                    prev.map((t) =>
                                      t.idTarefaCasoTipologia === tipologia.idTarefaCasoTipologia
                                        ? { ...t, concluido: valorAnterior }
                                        : t
                                    )
                                  );
                                  openNotification({
                                    isOpen: true,
                                    message: "Erro ao atualizar status da tipologia. Tente novamente.",
                                    type: "error",
                                  });
                                }
                              }
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={async () => {
                              if (
                                window.confirm(
                                  "Deseja remover esta tipologia do caso?"
                                ) &&
                                casoAtual?.idTarefaCaso
                              ) {
                                try {
                                  await removerTipologiaDoCaso(
                                    casoAtual.idTarefaCaso,
                                    tipologia.idTarefaCasoTipologia
                                  );
                                  setTipologias((prev) =>
                                    prev.filter(
                                      (t) =>
                                        t.idTarefaCasoTipologia !==
                                        tipologia.idTarefaCasoTipologia
                                    )
                                  );
                                  openNotification({
                                    isOpen: true,
                                    message: "Tipologia removida com sucesso!",
                                    type: "success",
                                  });
                                } catch (error) {
                                  console.error("Erro ao remover tipologia:", error);
                                }
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* 24/08: tipologias PENDENTES (Caso novo) — gravadas só no SALVAR */}
                    {tipologiasPendentes.map((pendente, index) => (
                      <TableRow key={pendente.chave} sx={{ backgroundColor: "#fffde7" }}>
                        <TableCell>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              backgroundColor: "#FBC02D",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                            }}
                          >
                            {tipologias.length + index + 1}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={`${pendente.descricao || ""} — será gravada ao salvar o Caso`}
                            arrow
                          >
                            <Typography variant="body2">{pendente.codigo}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{pendente.nomeLinha || "-"}</TableCell>
                        <TableCell>{pendente.nomeProjetista || "-"}</TableCell>
                        <TableCell>{pendente.origem === "nova" ? "Sim" : "Não"}</TableCell>
                        <TableCell>
                          <Checkbox checked={false} disabled size="small" />
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              pendente.origem === "copia"
                                ? "Remove todas as tipologias desta cópia (ainda não gravadas)"
                                : "Remove a tipologia (ainda não gravada)"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleRemoverPendente(pendente)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {/* 24/08: excluir Caso criado sem querer / sem uso — só em Caso existente */}
        {!!idCasoAtual && (
          <Button
            onClick={handleExcluirCaso}
            variant="outlined"
            color="error"
            disabled={loading}
            startIcon={<DeleteIcon />}
            sx={{
              textTransform: "uppercase",
              fontWeight: 600,
              minWidth: 120,
              mr: "auto",
            }}
          >
            EXCLUIR
          </Button>
        )}
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          CANCELAR
        </Button>
        <Button
          onClick={handleSalvar}
          variant="contained"
          color="primary"
          disabled={loading || (isEdicao && !hasChanges)}
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            minWidth: 120,
          }}
        >
          {loading ? <CircularProgress size={20} /> : "SALVAR"}
        </Button>
      </DialogActions>

      {/* Modais auxiliares — 24/08: também no Caso NOVO, em modo "selecionar sem
          salvar" (as escolhas ficam pendentes e gravam apenas no SALVAR) */}
      <ModalImportarTipologiasMOF
        isOpen={modalImportarMOFOpen}
        onClose={() => setModalImportarMOFOpen(false)}
        idTarefaCaso={casoAtual?.idTarefaCaso ?? 0}
        onSelecionarSemSalvar={
          !casoAtual?.idTarefaCaso ? handleSelecionarMOFPendente : undefined
        }
        onTipologiasImportadas={async () => {
          // Recarregar caso completo para atualizar tipologias
          if (casoAtual?.idTarefaCaso) {
            const casoCompleto = await obterCasoCompleto(casoAtual.idTarefaCaso);
            if (casoCompleto) {
              setTipologias(casoCompleto.tipologias || []);
              setCasoLocal(casoCompleto);
            }
          }
        }}
      />
      <ModalCriarTipologiasNovas
        isOpen={modalCriarTipologiasOpen}
        onClose={() => setModalCriarTipologiasOpen(false)}
        idTarefaCaso={casoAtual?.idTarefaCaso ?? 0}
        onSelecionarSemSalvar={
          !casoAtual?.idTarefaCaso ? handleCriarNovasPendente : undefined
        }
        onTipologiasCriadas={async () => {
          // Recarregar caso completo para atualizar tipologias
          if (casoAtual?.idTarefaCaso) {
            const casoCompleto = await obterCasoCompleto(casoAtual.idTarefaCaso);
            if (casoCompleto) {
              setTipologias(casoCompleto.tipologias || []);
              setCasoLocal(casoCompleto);
            }
          }
        }}
      />
      <ModalCopiarCasos
        isOpen={modalCopiarCasosOpen}
        onClose={() => setModalCopiarCasosOpen(false)}
        idTarefaCaso={casoAtual?.idTarefaCaso ?? 0}
        idTarefaAtividade={idTarefaAtividade}
        onSelecionarSemSalvar={
          !casoAtual?.idTarefaCaso ? handleCopiarCasoPendente : undefined
        }
        onCasosCopiados={async () => {
          // Recarregar caso completo para atualizar tipologias
          if (casoAtual?.idTarefaCaso) {
            const casoCompleto = await obterCasoCompleto(casoAtual.idTarefaCaso);
            if (casoCompleto) {
              setTipologias(casoCompleto.tipologias || []);
              setCasoLocal(casoCompleto);
            }
          }
        }}
      />

      {/* F04R-4 (reteste 21/08, item 9): editor de Detalhes expandido — mesma
          value/onChange do inline (padrão do PainelComentarios, refs separados) */}
      <Dialog
        open={editorExpandido}
        onClose={() => setEditorExpandido(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Detalhes do caso
          <IconButton
            onClick={() => setEditorExpandido(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <FullscreenExit />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, mb: 5 }}>
            <ReactQuill
              ref={quillExpandidoRef}
              theme="snow"
              value={formData.detalhes || ""}
              onChange={(value) => handleChange("detalhes", value)}
              modules={quillModules}
              formats={quillFormats}
              style={{ height: 400 }}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={6}>
            <Button
              variant="contained"
              onClick={() => setEditorExpandido(false)}
            >
              Fechar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

